import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiUrls } from '../../utils/apiUrls';
import { callAPI } from '../../utils/apiUtils';

const dummyWishlistItems = [
  {
    id: 1,
    productId: 1,
    image: "/image/item-1.png",
    brand: "Continental",
    name: "Premium All-Season Tire - Continental TrueContact",
    rating: 5,
    reviews: 128,
    price: 145.99,
    originalPrice: 159.94,
    discount: 30,
    isFavorite: true,
    addedDate: "2025-12-15T10:30:00Z",
    inStock: true
  },
  {
    id: 2,
    productId: 2,
    image: "/image/item-2.png",
    brand: "Michelin",
    name: "Performance Winter Tire - Michelin X-Ice",
    rating: 4,
    reviews: 89,
    price: 189.99,
    originalPrice: 199.99,
    discount: 15,
    isFavorite: true,
    addedDate: "2025-12-14T14:25:00Z",
    inStock: true
  },
  {
    id: 3,
    productId: 3,
    image: "/image/item-3.png",
    brand: "Bridgestone",
    name: "Turanza T005 - Quiet Comfort Touring",
    rating: 4,
    reviews: 56,
    price: 135.50,
    originalPrice: 135.50,
    discount: 0,
    isFavorite: true,
    addedDate: "2025-12-13T09:15:00Z",
    inStock: true
  },
  {
    id: 4,
    productId: 4,
    image: "/image/item-4.png",
    brand: "Pirelli",
    name: "P Zero - Ultra High Performance",
    rating: 5,
    reviews: 203,
    price: 249.99,
    originalPrice: 279.99,
    discount: 20,
    isFavorite: true,
    addedDate: "2025-12-12T16:45:00Z",
    inStock: true
  }
];

const dummyUserData = {
  name: "Travis Barker",
  email: "travisbarker@gmail.com",
  avatar: null
};

export const fetchWishlist = createAsyncThunk(
  'wishlist/fetchWishlist',
  async (_, { rejectWithValue }) => {
    try {
      const response = await callAPI(
        apiUrls.productSaveListing,
        {},
        "GET",
        {}
      );

      if (response?.data?.success) {
        return response.data.data;
      } else {
        return rejectWithValue(response?.data?.message || 'Failed to fetch wishlist');
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch wishlist');
    }
  }
);

export const addToWishlist = createAsyncThunk(
  'wishlist/addToWishlist',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await callAPI(
        apiUrls.productSave,
        { product_id: productId },
        "POST",
        {}
      );

      if (response?.data?.success) {
        return response.data.data;
      } else {
        return rejectWithValue(response?.data?.message || 'Failed to add to wishlist');
      }

    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add to wishlist');
    }
  }
);

export const removeFromWishlist = createAsyncThunk(
  'wishlist/removeFromWishlist',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await callAPI(
        `${apiUrls.productSave}`,
        { product_id: productId },
        "POST",
        {}
      );

      if (response?.data?.success) {
        return productId;
      } else {
        return rejectWithValue(response?.data?.message || 'Failed to remove from wishlist');
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to remove from wishlist');
    }
  }
);

export const clearWishlist = createAsyncThunk(
  'wishlist/clearWishlist',
  async (_, { rejectWithValue }) => {
    try {
      await new Promise(resolve => setTimeout(resolve, 600));
      return [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to clear wishlist');
    }
  }
);

export const addAllToCart = createAsyncThunk(
  'wishlist/addAllToCart',
  async (_, { getState, rejectWithValue }) => {
    try {
      await new Promise(resolve => setTimeout(resolve, 800));

      const state = getState();
      const wishlistItems = state.wishlist?.wishlistItems || [];

      return {
        success: true,
        count: wishlistItems.length,
        message: `Added ${wishlistItems.length} items to cart successfully!`
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add items to cart');
    }
  }
);

export const fetchUserProfile = createAsyncThunk(
  'wishlist/fetchUserProfile',
  async (_, { rejectWithValue }) => {
    try {
      await new Promise(resolve => setTimeout(resolve, 300));
      return dummyUserData;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch user profile');
    }
  }
);

const wishlistSlice = createSlice({
  name: 'wishlist',
  initialState: {
    wishlistItems: [],
    wishlistIds: [],

    currentPage: 1,
    itemsPerPage: 10,
    totalResults: 0,
    totalPages: 0,

    userData: null,

    loading: false,
    wishlistLoading: false,
    addLoading: false,
    removeLoading: false,
    clearLoading: false,
    cartLoading: false,
    userLoading: false,

    message: {
      type: "",
      text: ""
    },

    error: null,

    addSuccess: false,
    removeSuccess: false,
    clearSuccess: false,
    cartSuccess: false
  },
  reducers: {
        syncWishlistIds: (state, action) => {
      state.wishlistIds = action.payload;
    },
    setWishlistPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setWishlistPerPage: (state, action) => {
      state.itemsPerPage = action.payload;
      state.currentPage = 1;
    },

    optimisticRemoveItem: (state, action) => {
      const productId = action.payload;
      state.wishlistItems = state.wishlistItems.filter(item =>
        item.id !== productId && item.productId !== productId
      );
      state.wishlistIds = state.wishlistIds.filter(id => id !== productId);
    },

    optimisticClearWishlist: (state) => {
      state.wishlistItems = [];
      state.wishlistIds = [];
    },

    setMessage: (state, action) => {
      state.message = {
        type: action.payload.type,
        text: action.payload.text
      };
    },

    clearMessage: (state) => {
      state.message = {
        type: "",
        text: ""
      };
    },

    clearWishlistError: (state) => {
      state.error = null;
    },

    clearAddSuccess: (state) => {
      state.addSuccess = false;
    },

    clearRemoveSuccess: (state) => {
      state.removeSuccess = false;
    },

    clearClearSuccess: (state) => {
      state.clearSuccess = false;
    },

    clearCartSuccess: (state) => {
      state.cartSuccess = false;
    },

    resetWishlistState: (state) => {
      state.wishlistItems = [];
      state.wishlistIds = [];
      state.currentPage = 1;
      state.itemsPerPage = 10;
      state.totalResults = 0;
      state.totalPages = 0;
      state.userData = null;
      state.message = { type: "", text: "" };
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
       .addCase(fetchWishlist.pending, (state) => {
        state.loading = true;
        state.wishlistLoading = true;
        state.error = null;
      })
      .addCase(fetchWishlist.fulfilled, (state, action) => {
        state.loading = false;
        state.wishlistLoading = false;
        const response = action.payload;
        let items = [];
        if (Array.isArray(response?.data)) {
          items = response.data;
        } else if (response?.data && typeof response.data === 'object') {
          items = [response.data];
        } else {
          items = [];
        }

        state.wishlistItems = items;
        state.wishlistIds = items.map(item => item.id);
        state.currentPage = response?.current_page || 1;
        state.itemsPerPage = response?.per_page || 10;
        state.totalResults = response?.total || 0;
        state.totalPages = response?.last_page || 1;
      })
      .addCase(fetchWishlist.rejected, (state, action) => {
        state.loading = false;
        state.wishlistLoading = false;
        state.error = action.payload || 'Failed to fetch wishlist';
        state.message = {
          type: 'error',
          text: action.payload || 'Failed to fetch wishlist'
        };
      })

      .addCase(fetchUserProfile.pending, (state) => {
        state.userLoading = true;
        state.error = null;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.userLoading = false;
        state.userData = action.payload;
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.userLoading = false;
        state.error = action.payload || 'Failed to fetch user profile';
      })

  .addCase(addToWishlist.pending, (state, action) => {
        state.addLoading = true;
        state.addSuccess = false;
        state.error = null;
        const productId = action.meta.arg;
        if (!state.wishlistIds.includes(productId)) {
          state.wishlistIds.push(productId);
        }
      })
      .addCase(addToWishlist.fulfilled, (state, action) => {
        state.addLoading = false;
        state.addSuccess = true;
        const { productId, data } = action.payload;
        
        if (!state.wishlistIds.includes(productId)) {
          state.wishlistIds.push(productId);
        }
        
        if (data && !state.wishlistItems.some(item => item.id == data.id)) {
          state.wishlistItems.push(data);
        }
        
        state.message = {
          type: 'success',
          text: 'Item added to wishlist successfully!'
        };
      })
      .addCase(addToWishlist.rejected, (state, action) => {
        state.addLoading = false;
        state.addSuccess = false;
        const productId = action.meta.arg;
        state.wishlistIds = state.wishlistIds.filter(id => id !== productId);
        state.error = action.payload || 'Failed to add to wishlist';
        state.message = {
          type: 'error',
          text: action.payload || 'Failed to add to wishlist'
        };
      })

        .addCase(removeFromWishlist.pending, (state, action) => {
        state.removeLoading = true;
        state.removeSuccess = false;
        state.error = null;
        
        const productId = action.meta.arg;
        state.wishlistItems = state.wishlistItems.filter(item =>
          item.id !== productId && item.productId !== productId
        );
        state.wishlistIds = state.wishlistIds.filter(id => id !== productId);
      })
      .addCase(removeFromWishlist.fulfilled, (state, action) => {
        state.removeLoading = false;
        state.removeSuccess = true;

        state.message = {
          type: 'success',
          text: 'Item removed from wishlist successfully!'
        };
      })
      .addCase(removeFromWishlist.rejected, (state, action) => {
        state.removeLoading = false;
        state.removeSuccess = false;
        
        const productId = action.meta.arg;
        if (!state.wishlistIds.includes(productId)) {
          state.wishlistIds.push(productId);
        }
        
        state.error = action.payload || 'Failed to remove from wishlist';
        state.message = {
          type: 'error',
          text: action.payload || 'Failed to remove from wishlist'
        };
      })

      .addCase(clearWishlist.pending, (state) => {
        state.clearLoading = true;
        state.clearSuccess = false;
        state.error = null;
      })
      .addCase(clearWishlist.fulfilled, (state) => {
        state.clearLoading = false;
        state.clearSuccess = true;
        state.wishlistItems = [];
        state.wishlistIds = [];

        state.message = {
          type: 'success',
          text: 'Wishlist cleared successfully!'
        };
      })
      .addCase(clearWishlist.rejected, (state, action) => {
        state.clearLoading = false;
        state.clearSuccess = false;
        state.error = action.payload || 'Failed to clear wishlist';
        state.message = {
          type: 'error',
          text: action.payload || 'Failed to clear wishlist'
        };
      })

      .addCase(addAllToCart.pending, (state) => {
        state.cartLoading = true;
        state.cartSuccess = false;
        state.error = null;
      })
      .addCase(addAllToCart.fulfilled, (state, action) => {
        state.cartLoading = false;
        state.cartSuccess = true;

        state.message = {
          type: 'success',
          text: action.payload.message || `Added ${action.payload.count} items to cart successfully!`
        };
      })
      .addCase(addAllToCart.rejected, (state, action) => {
        state.cartLoading = false;
        state.cartSuccess = false;
        state.error = action.payload || 'Failed to add items to cart';
        state.message = {
          type: 'error',
          text: action.payload || 'Failed to add items to cart'
        };
      });
  }
});

export const {
  optimisticRemoveItem,
  optimisticClearWishlist,
  setMessage,
  clearMessage,
  clearWishlistError,
  clearAddSuccess,
  clearRemoveSuccess,
    syncWishlistIds,
  clearClearSuccess,
  clearCartSuccess,
  resetWishlistState
} = wishlistSlice.actions;

export default wishlistSlice.reducer;